/*
 * Rooté Bloom hair quiz.
 *
 * Drives sections/rb-hair-quiz.liquid: a linear step machine, a weighted scorer, and an
 * email gate that posts to Shopify's customer form.
 *
 * The weight table below is the whole recommendation engine. docs/hair-quiz.md
 * explains how to read and retune it, and why the questions are worded the way they are.
 */

if (!customElements.get('rb-hair-quiz')) {
  /*
   * Versioned. The v1 shape stored `scalp` and `concern`, which no longer score, so a
   * session that was mid-quiz across the deploy must start clean rather than restore
   * answers the scorer will silently ignore.
   */
  const STORAGE_KEY = 'rb-hair-quiz-v2';
  /* Set just before the signup navigates, so the return load knows the quiz owns it. */
  const PENDING_SIGNUP_KEY = 'rb-hair-quiz-pending-signup';

  const SLOTS = [
    'shampoo',
    'tharaa',
    'herbal_gold',
    'serum',
    'cream',
    'mask',
    'butter',
    'brush',
  ];

  /* Breaks ties, so the same answers always produce the same grid. */
  const SLOT_PRIORITY = [
    'mask',
    'tharaa',
    'butter',
    'cream',
    'serum',
    'herbal_gold',
    'shampoo',
    'brush',
  ];

  /*
   * Tharaa Elixir and Herbal Gold do the same job with a different botanical story.
   * Recommending both wastes a slot, so only the higher scorer survives.
   */
  const SISTER_SKUS = ['tharaa', 'herbal_gold'];

  /*
   * Points per answer. Negatives matter as much as positives — they are what keeps a
   * 250 ml butter away from someone with fine, oily hair.
   */
  const WEIGHTS = {
    type_straight: { serum: 2, shampoo: 1, tharaa: 1, butter: -2 },
    type_wavy: { serum: 2, cream: 1, tharaa: 1 },
    type_curly: { cream: 2, mask: 2, butter: 1 },
    type_coily: { butter: 3, mask: 2, cream: 1 },

    /*
     * One multi-select rather than a scalp question and a concern question. A shopper
     * can be curly AND dry AND damaged at once, and forcing one answer per axis made
     * her discard two thirds of what she came to solve.
     */
    condition_oily_scalp: { shampoo: 3, mask: 1, brush: 1, butter: -2, cream: -1 },
    condition_flaky_scalp: { shampoo: 3, brush: 2, mask: 1, tharaa: 1 },
    condition_dry_scalp: { tharaa: 2, mask: 1, butter: 1, cream: 1 },
    condition_dry_hair: { cream: 3, mask: 2, butter: 2 },
    condition_damaged: { butter: 3, cream: 2, mask: 2 },
    condition_frizz: { serum: 3, butter: 2, cream: 1 },
    condition_flat: { tharaa: 3, herbal_gold: 3, brush: 2 },
    condition_dull: { serum: 3, tharaa: 1, herbal_gold: 1 },
    condition_balanced: { herbal_gold: 1, serum: 1 },

    /*
     * Porosity, asked as observable behaviour. Nobody can answer "what is your
     * porosity?", and a guessed answer is noise the scorer cannot tell from a real one.
     * `unsure` is deliberately weightless — neutral beats a coin flip.
     */
    water_low: { serum: 2, shampoo: 1, butter: -2, cream: -1 },
    water_medium: { cream: 1, tharaa: 1 },
    water_high: { butter: 2, mask: 2, cream: 1, serum: 1 },
    water_unsure: {},

    thickness_fine: { serum: 2, tharaa: 1, herbal_gold: 1, butter: -2, cream: -1 },
    thickness_medium: { cream: 1, mask: 1 },
    thickness_coarse: { butter: 2, mask: 2, herbal_gold: 1, serum: -1 },

    routine_quick: { serum: 3, shampoo: 1, mask: -2, butter: -1, brush: -1 },
    routine_weekly: { mask: 2, cream: 2, tharaa: 1 },
    routine_ritual: { brush: 3, mask: 2, tharaa: 2, herbal_gold: 1, shampoo: 1 },
  };

  const ANSWER_STEPS = ['name', 'type', 'condition', 'water', 'thickness', 'routine'];

  /*
   * Steps whose answer is an array. The cap is load-bearing: ticking every box would
   * pile on enough positive weight to drown the negatives, and the negatives are the
   * only thing keeping a heavy butter away from fine, oily hair.
   */
  const MULTI_STEPS = { condition: 3 };

  /* "Nothing in particular" cannot be true alongside a complaint. */
  const EXCLUSIVE_ANSWERS = { condition: 'balanced' };

  /*
   * Mirrors the `components` lists in scripts/bundles_data.py. Keep the two in step —
   * a bundle is a real product built in the Fast Bundle app, so this file cannot
   * derive its contents.
   */
  const BUNDLES = {
    growth: ['tharaa', 'herbal_gold', 'brush'],
    color_repair: ['cream', 'serum'],
    healthy_scalp: ['shampoo', 'serum'],
    growth_repair: ['tharaa', 'mask', 'serum'],
    sidr_rebuild: ['butter', 'mask'],
    luxury_spa: ['butter', 'mask', 'serum', 'brush'],
  };

  /* A bundle needs a majority of its contents to have scored before it is offered. */
  const BUNDLE_MATCH_RATIO = 0.6;

  /* Conditions that warrant pointing at a dermatologist rather than a routine. */
  const MEDICAL_REFERRAL_ANSWERS = ['flaky_scalp'];

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  class RbHairQuiz extends HTMLElement {
    connectedCallback() {
      this.steps = Array.from(this.querySelectorAll('[data-step]'));
      if (!this.steps.length) return;

      this.resultsCount = Number(this.dataset.resultsCount) || 3;
      this.announcer = this.querySelector('[data-announcer]');
      this.progress = this.querySelector('[data-progress]');
      this.emailForm = this.querySelector('.rb-quiz__email-form');
      this.answers = this.readStoredAnswers();
      this.index = 0;

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('keydown', this.onKeydown);
      this.emailForm?.addEventListener('submit', this.onEmailSubmit);

      this.restoreSelections();

      const resumed = this.resumeAfterSignup();
      this.render();
      if (resumed) {
        this.showResults();
        this.scrollIntoView({ block: 'start' });
      }
    }

    /* ---------- state ---------- */

    readStoredAnswers() {
      if (!window.theme?.config?.hasSessionStorage) return {};
      try {
        return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
      } catch (error) {
        return {};
      }
    }

    persistAnswers() {
      if (!window.theme?.config?.hasSessionStorage) return;
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.answers));
      } catch (error) {
        /* A full or disabled store is not worth interrupting the quiz for. */
      }
    }

    selected(key) {
      const value = this.answers[key];
      return Array.isArray(value) ? value : [];
    }

    restoreSelections() {
      Object.entries(this.answers).forEach(([key, value]) => {
        const fields = Array.from(this.querySelectorAll(`[data-answer="${key}"]`));

        if (Array.isArray(value)) {
          fields.forEach((field) => {
            if (field.type === 'checkbox') field.checked = value.includes(field.value);
          });
          return;
        }

        const radio = fields.find((field) => field.type === 'radio' && field.value === value);
        if (radio) {
          radio.checked = true;
          return;
        }
        const textField = this.querySelector(`input[type="text"][data-answer="${key}"]`);
        if (textField) textField.value = value;
      });
    }

    /* ---------- step machine ---------- */

    stepName(index) {
      return this.steps[index]?.dataset.step;
    }

    nextIndex(from) {
      return Math.min(from + 1, this.steps.length - 1);
    }

    previousIndex(from) {
      return Math.max(from - 1, 0);
    }

    render() {
      const activeStep = this.steps[this.index];

      this.steps.forEach((step, index) => {
        step.hidden = index !== this.index;
      });

      this.interpolateCopy(activeStep);
      this.updateProgress();
      this.clearErrors(activeStep);

      const heading = activeStep.querySelector('.rb-quiz__question, .rb-quiz__title');
      if (this.announcer && heading) this.announcer.textContent = heading.textContent.trim();
    }

    /*
     * Question copy may contain {name}. Before the name is known — the visitor went
     * back and cleared it — drop a leading "{name}, " outright so the sentence still
     * reads as one, rather than starting with a comma.
     */
    fillName(template) {
      const name = this.answers.name;
      if (name) return template.replace(/\{name\}/g, name);
      return template
        .replace(/^\{name\},\s*/, '')
        .replace(/\{name\}/g, '')
        .trim();
    }

    interpolateCopy(step) {
      step.querySelectorAll('[data-question-text]').forEach((node) => {
        node.textContent = this.fillName(node.dataset.questionText);
      });
    }

    updateProgress() {
      if (!this.progress) return;
      const questions = this.steps.filter((step) => ANSWER_STEPS.includes(step.dataset.step));
      const current = questions.indexOf(this.steps[this.index]);

      if (current === -1) {
        this.progress.hidden = true;
        return;
      }

      this.progress.hidden = false;
      this.progress.querySelector('[data-progress-current]').textContent = current + 1;
      this.progress.querySelector('[data-progress-total]').textContent = questions.length;
      this.progress.querySelector('[data-progress-bar]').style.width =
        `${((current + 1) / questions.length) * 100}%`;
    }

    /* ---------- validation ---------- */

    clearErrors(step) {
      step.querySelectorAll('[data-error]').forEach((node) => {
        node.hidden = true;
      });
    }

    showError(step, key) {
      const node = step.querySelector(`[data-error="${key}"]`);
      if (node) node.hidden = false;
    }

    validateCurrentStep() {
      const step = this.steps[this.index];
      const name = step.dataset.step;

      if (name === 'name') {
        if (this.answers.name) return true;
        this.showError(step, 'name');
        step.querySelector('input[type="text"]')?.focus();
        return false;
      }

      if (name in MULTI_STEPS) {
        if (this.selected(name).length) return true;
        this.showError(step, 'choice');
        return false;
      }

      if (ANSWER_STEPS.includes(name)) {
        if (this.answers[name]) return true;
        this.showError(step, 'choice');
        return false;
      }

      return true;
    }

    /* ---------- events ---------- */

    onClick = (event) => {
      const action = event.target.closest('[data-action]')?.dataset.action;
      if (!action) return;

      if (action === 'next') {
        if (!this.validateCurrentStep()) return;
        this.goTo(this.nextIndex(this.index));
      } else if (action === 'back') {
        this.goTo(this.previousIndex(this.index));
      } else if (action === 'restart') {
        this.restart();
      }
    };

    onChange = (event) => {
      const field = event.target.closest('[data-answer]');
      if (!field) return;

      if (field.type === 'checkbox') {
        this.onMultiChange(field);
        return;
      }
      if (field.type !== 'radio') return;

      this.answers[field.dataset.answer] = field.value;
      this.persistAnswers();
      this.clearErrors(this.steps[this.index]);

      /* Let the selected state paint before moving on. */
      const delay = window.theme?.config?.motionReduced ? 0 : 260;
      window.setTimeout(() => {
        if (this.steps[this.index]?.contains(field)) this.goTo(this.nextIndex(this.index));
      }, delay);
    };

    /*
     * A multi-select never auto-advances — the visitor is not finished until she says
     * so with Continue.
     */
    onMultiChange(field) {
      const key = field.dataset.answer;
      const step = this.steps[this.index];
      const exclusive = EXCLUSIVE_ANSWERS[key];
      const limit = MULTI_STEPS[key] || Infinity;
      let chosen = this.selected(key);

      if (!field.checked) {
        chosen = chosen.filter((value) => value !== field.value);
      } else if (exclusive && field.value === exclusive) {
        chosen = [exclusive];
      } else {
        chosen = chosen.filter((value) => value !== exclusive);
        if (chosen.length >= limit) {
          /* Refuse the tick and say why, rather than accepting it and not scoring it. */
          field.checked = false;
          this.clearErrors(step);
          this.showError(step, 'max');
          return;
        }
        chosen.push(field.value);
      }

      /* Re-sync the boxes: the exclusive rule can clear ticks the visitor did not touch. */
      this.querySelectorAll(`[data-answer="${key}"]`).forEach((input) => {
        input.checked = chosen.includes(input.value);
      });

      if (chosen.length) this.answers[key] = chosen;
      else delete this.answers[key];

      this.persistAnswers();
      this.clearErrors(step);
    }

    onInput = (event) => {
      const field = event.target.closest('input[type="text"][data-answer]');
      if (!field) return;
      const value = field.value.trim();
      if (value) {
        this.answers[field.dataset.answer] = value;
      } else {
        delete this.answers[field.dataset.answer];
      }
      this.persistAnswers();
    };

    onKeydown = (event) => {
      if (event.key !== 'Enter') return;
      const field = event.target.closest('input[type="text"][data-answer]');
      if (!field) return;
      event.preventDefault();
      if (this.validateCurrentStep()) this.goTo(this.nextIndex(this.index));
    };

    goTo(index) {
      if (index === this.index) return;
      this.index = index;
      this.render();

      if (this.stepName(index) === 'results') this.showResults();

      const focusTarget = this.steps[index].querySelector(
        'input[type="text"], input[type="email"], input[type="radio"], input[type="checkbox"], button'
      );
      focusTarget?.focus({ preventScroll: true });
      this.scrollIntoViewIfNeeded();
    }

    scrollIntoViewIfNeeded() {
      const { top } = this.getBoundingClientRect();
      if (top >= 0) return;
      this.scrollIntoView({
        behavior: window.theme?.config?.motionReduced ? 'auto' : 'smooth',
        block: 'start',
      });
    }

    restart() {
      this.answers = {};
      this.persistAnswers();
      this.querySelectorAll('[data-answer]').forEach((field) => {
        if (field.type === 'radio' || field.type === 'checkbox') field.checked = false;
        else field.value = '';
      });
      this.goTo(0);
    }

    /* ---------- email ---------- */

    /*
     * The signup is submitted NATIVELY, not by fetch.
     *
     * When a store has Shopify's spam protection on, /contact rejects any POST
     * without a CAPTCHA token ("Missing CAPTCHA token", HTTP 400). That token is
     * appended by Shopify's own script, which hooks the form's native submit — a
     * hand-rolled fetch bypasses it and can never succeed. So the page really does
     * navigate here.
     *
     * Answers survive the round trip in sessionStorage, and Shopify returns to this
     * page with form.posted_successfully?, which resumeAfterSignup() picks up to jump
     * straight to the results.
     */
    onEmailSubmit = (event) => {
      const step = this.steps[this.index];
      const emailField = this.emailForm.querySelector('input[type="email"]');
      const email = emailField.value.trim();

      if (!EMAIL_PATTERN.test(email)) {
        event.preventDefault();
        this.showError(step, 'email');
        emailField.focus();
        return;
      }

      this.clearErrors(step);
      this.emailForm.querySelector('[data-quiz-tags]').value = this.buildTags().join(',');
      this.emailForm.querySelector('[data-quiz-first-name]').value = this.answers.name || '';
      this.persistAnswers();

      try {
        sessionStorage.setItem(PENDING_SIGNUP_KEY, '1');
      } catch (error) {
        /* Without it we simply do not suppress the footer's duplicate notice. */
      }

      const submitButton = this.emailForm.querySelector('button[type="submit"]');
      submitButton.classList.add('loading');
      submitButton.setAttribute('aria-disabled', 'true');
      /* No preventDefault — let Shopify (and its CAPTCHA hook) take the form. */
    };

    /*
     * `form.posted_successfully?` is true for EVERY {% form 'customer' %} on the page,
     * not just the one that was submitted — so the footer's email-signup block renders
     * its own "thanks for subscribing" banner off the back of a quiz signup, and keeps
     * rendering it on reload while the success parameter stays in the URL.
     *
     * Clear both: hide the foreign banners, and drop the query string.
     */
    suppressForeignSignupNotices() {
      document
        .querySelectorAll('.email-signup__message--success')
        .forEach((notice) => {
          if (!this.contains(notice)) notice.hidden = true;
        });

      if (window.location.search && window.history?.replaceState) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    }

    /*
     * Returns true when we came back from a signup and should skip to the results.
     * Liquid stamps [data-quiz-posted] on success and [data-quiz-post-failed] when
     * the form came back with errors.
     */
    resumeAfterSignup() {
      let ownsSignup = false;
      try {
        ownsSignup = sessionStorage.getItem(PENDING_SIGNUP_KEY) === '1';
        sessionStorage.removeItem(PENDING_SIGNUP_KEY);
      } catch (error) {
        /* Treated as not ours, which only means the footer banner stays. */
      }
      /*
       * Only act on a post this quiz started. Otherwise submitting the footer's own
       * newsletter form on this page would also set form.posted_successfully? and
       * throw the visitor into the results.
       */
      if (!ownsSignup) return false;
      this.suppressForeignSignupNotices();

      if (this.querySelector('[data-quiz-post-failed]')) {
        const notice = this.querySelector('[data-email-notice]');
        if (notice) {
          notice.textContent = notice.dataset.failureMessage || '';
          notice.hidden = !notice.textContent;
        }
      } else if (!this.querySelector('[data-quiz-posted]')) {
        return false;
      }

      /* Only meaningful if the answers actually survived the round trip. */
      if (!this.answers.type) return false;

      const resultsIndex = this.steps.findIndex((step) => step.dataset.step === 'results');
      if (resultsIndex === -1) return false;

      this.index = resultsIndex;
      return true;
    }

    buildTags() {
      const tags = ['hair-quiz'];
      ['type', 'condition', 'water', 'thickness', 'routine'].forEach((key) => {
        const value = this.answers[key];
        if (!value) return;
        if (Array.isArray(value)) value.forEach((item) => tags.push(`${key}:${item}`));
        else tags.push(`${key}:${value}`);
      });
      return tags;
    }

    /* ---------- scoring ---------- */

    score() {
      const totals = Object.fromEntries(SLOTS.map((slot) => [slot, 0]));
      const contributions = Object.fromEntries(SLOTS.map((slot) => [slot, {}]));

      const apply = (answerKey) => {
        const weights = WEIGHTS[answerKey];
        if (!weights) return;
        Object.entries(weights).forEach(([slot, points]) => {
          if (!(slot in totals)) return;
          totals[slot] += points;
          contributions[slot][answerKey] = points;
        });
      };

      Object.entries(this.answers).forEach(([question, value]) => {
        const values = Array.isArray(value) ? value : [value];
        values.forEach((item) => apply(`${question}_${item}`));
      });

      return { totals, contributions };
    }

    rank(totals) {
      const available = SLOTS.filter((slot) => this.querySelector(`[data-slot="${slot}"]`));

      const ordered = available.sort((a, b) => {
        if (totals[b] !== totals[a]) return totals[b] - totals[a];
        return SLOT_PRIORITY.indexOf(a) - SLOT_PRIORITY.indexOf(b);
      });

      const [first, second] = SISTER_SKUS;
      /* Only worth dropping one when the merchant actually assigned both. */
      const bothAssigned = available.includes(first) && available.includes(second);
      const loser = bothAssigned ? this.lowerScorer(first, second, totals) : null;

      return ordered.filter((slot) => slot !== loser).slice(0, this.resultsCount);
    }

    lowerScorer(first, second, totals) {
      if (totals[first] !== totals[second]) {
        return totals[first] > totals[second] ? second : first;
      }
      return SLOT_PRIORITY.indexOf(first) < SLOT_PRIORITY.indexOf(second) ? second : first;
    }

    /*
     * The bundle offered alongside the individual cards.
     *
     * Matching runs on the RAW scores, before rank() drops a sister SKU. That exclusion
     * exists to stop two near-identical oils eating two recommendation slots, which is
     * irrelevant to a bundle — and the Hair Growth Bundle contains both of them, so
     * matching post-exclusion would make it unreachable.
     *
     * The winner is the bundle with the most matched components, not the highest ratio:
     * a four-product bundle that fits three of her needs is a better answer than a
     * two-product one that fits two.
     */
    matchBundle(totals) {
      const pool = SLOTS.filter((slot) => totals[slot] > 0)
        .sort((a, b) => {
          if (totals[b] !== totals[a]) return totals[b] - totals[a];
          return SLOT_PRIORITY.indexOf(a) - SLOT_PRIORITY.indexOf(b);
        })
        .slice(0, this.resultsCount + 2);

      let best = null;

      Object.entries(BUNDLES).forEach(([key, components]) => {
        /* A bundle the merchant never assigned is skipped, exactly like a product slot. */
        if (!this.querySelector(`[data-bundle="${key}"]`)) return;

        const matched = components.filter((slot) => pool.includes(slot));
        if (matched.length / components.length < BUNDLE_MATCH_RATIO) return;

        const strength = matched.reduce((sum, slot) => sum + totals[slot], 0);
        const better =
          !best ||
          matched.length > best.count ||
          (matched.length === best.count && strength > best.strength);

        if (better) best = { key, count: matched.length, strength };
      });

      return best?.key || null;
    }

    /*
     * Why a product surfaced, phrased from the answer that contributed most to it.
     * The sentences come from the section's `data-quiz-reasons` payload, which Liquid
     * renders through the `t` filter — an asset cannot read a locale file, so keeping
     * them here would have pinned the result cards to English.
     */
    get reasons() {
      if (!this._reasons) {
        this._reasons = this.readPayload('[data-quiz-reasons]');
      }
      return this._reasons;
    }

    /* Short trait fragments plus the porosity explainer, same rationale as `reasons`. */
    get profileCopy() {
      if (!this._profileCopy) {
        this._profileCopy = this.readPayload('[data-quiz-profile]');
      }
      return this._profileCopy;
    }

    readPayload(selector) {
      const node = this.querySelector(selector);
      try {
        return node ? JSON.parse(node.textContent) : {};
      } catch (error) {
        return {};
      }
    }

    /*
     * The strongest contributing answer, preferring one no other card has already used —
     * three cards all reading "picked for coily hair" tells the visitor nothing about
     * why the three differ.
     */
    reasonFor(slot, contributions, used = new Set()) {
      const reasons = this.reasons;
      const ranked = Object.entries(contributions[slot] || {})
        .filter(([answerKey, points]) => points > 0 && reasons[answerKey])
        .sort((a, b) => b[1] - a[1])
        .map(([answerKey]) => reasons[answerKey]);
      if (!ranked.length) return '';

      return ranked.find((reason) => !used.has(reason)) || ranked[0];
    }

    /*
     * Her answers read back as one profile — "curly, dry scalp, high porosity". This is
     * the only place the word porosity appears: she is told what she has after
     * describing it, never asked to name it up front.
     */
    renderProfile() {
      const copy = this.profileCopy;
      const traitsNode = this.querySelector('[data-profile-traits]');
      const noteNode = this.querySelector('[data-profile-note]');

      if (traitsNode) {
        const keys = [
          this.answers.type ? `type_${this.answers.type}` : null,
          ...this.selected('condition').map((value) => `condition_${value}`),
          this.answers.water ? `water_${this.answers.water}` : null,
        ];
        const traits = keys.map((key) => key && copy[key]).filter(Boolean);
        traitsNode.textContent = traits.join(copy.separator || ', ');
        traitsNode.hidden = !traits.length;
      }

      if (noteNode) {
        const note = this.answers.water ? copy[`note_${this.answers.water}`] : '';
        noteNode.textContent = note || '';
        noteNode.hidden = !note;
      }
    }

    showResults() {
      const { totals, contributions } = this.score();
      const winners = this.rank(totals);

      /* Walk in rank order so the strongest match gets first claim on a reason. */
      const usedReasons = new Set();
      const reasonBySlot = new Map();
      winners.forEach((slot) => {
        const reason = this.reasonFor(slot, contributions, usedReasons);
        if (reason) usedReasons.add(reason);
        reasonBySlot.set(slot, reason);
      });

      this.querySelectorAll('[data-slot]').forEach((card) => {
        const position = winners.indexOf(card.dataset.slot);
        card.hidden = position === -1;
        card.style.order = position === -1 ? '' : String(position);

        const reasonNode = card.querySelector('[data-reason]');
        if (!reasonNode) return;
        const reason = position === -1 ? '' : reasonBySlot.get(card.dataset.slot);
        reasonNode.textContent = reason || '';
        reasonNode.hidden = !reason;
      });

      this.renderProfile();
      this.renderBundle(this.matchBundle(totals));
      this.renderReferral();

      const empty = this.querySelector('[data-results-empty]');
      if (empty) empty.hidden = winners.length > 0;
    }

    renderBundle(winner) {
      const panel = this.querySelector('[data-bundles]');
      if (!panel) return;

      this.querySelectorAll('[data-bundle]').forEach((card) => {
        card.hidden = card.dataset.bundle !== winner;
      });
      panel.hidden = !winner;
    }

    renderReferral() {
      const notice = this.querySelector('[data-referral-notice]');
      if (!notice) return;
      notice.hidden = !this.selected('condition').some((value) =>
        MEDICAL_REFERRAL_ANSWERS.includes(value)
      );
    }
  }

  customElements.define('rb-hair-quiz', RbHairQuiz);
}
